import { AppConst, UtilsConst } from '@/constants'
import { StoreConst } from '@/store/constants'
import { apiRequest, onApiCatchError } from '@/store/modules/ApiRequestHelper'
import * as pipeline from '@/store/modules/app/pipeline'
import {
  ContextProcessingStatusResponse,
  CreateSecurityCertificatePayload,
  LibraryCategoryCreatePayload,
  LibraryEntry,
  LibraryReferenceAsset,
  LibraryReferenceAssetPayload,
  LoadContextProcessingStatusResult,
  upsertLibraryCategoriesPayload,
} from '@/store/modules/teamlibrary/types'
import { QueueIdResult, UploadThumbnailRequest } from '@/store/types'
import { TenantHelpers } from '@/utils/tenant-helpers'
import Axios, { AxiosRequestConfig } from 'axios'

declare var consoleError: any
declare var consoleWarn: any

export default {
  loadTenantsLibrary(context: any, urlParams: any): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    if (tenantId == null || tenantId === TenantHelpers.InvalidTenantID) {
      consoleWarn('loadTenantsLibrary called with invalid tenantId:', tenantId)
      return Promise.resolve(false)
    }

    const url = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${AppConst.APIEndpoint.libraryUrlV2}`

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url,
        params: urlParams,
      },
      context,
      'updateModelsLibrary',
      `getting tenant library`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  loadLibraryCategories(context: any, tenantId?: number): Promise<boolean> {
    const url: string = tenantId != null && tenantId !== TenantHelpers.InvalidTenantID
      ? `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${AppConst.APIEndpoint.libraryCategoriesUrlV2}`
      : AppConst.APIEndpoint.libraryCategoriesUrl
    const mutationName = tenantId != null && tenantId !== TenantHelpers.InvalidTenantID
      ? 'updateLibraryCategories'
      : 'updateLibraryPublicCategories'

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url,
      },
      context,
      mutationName,
      `getting library ${tenantId != null && tenantId !== TenantHelpers.InvalidTenantID ? 'tenant' : 'public'} categories`
    )
  },
  loadLibraryCategoriesUsage(context: any, tenantId: number): Promise<boolean> {
    let myTenantID: number = tenantId

    if (myTenantID == null || myTenantID === TenantHelpers.InvalidTenantID) {
      myTenantID = this.getMyTenantID(context.rootState.app)
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.APIEndpoint.tenantsV1Url}/${myTenantID}${AppConst.APIEndpoint.libraryCategoriesUrlV2}${AppConst.APIEndpoint.libraryCategoriesUrlUsageUrl}`,
      },
      context,
      'updateLibraryCategoriesUsage',
      `getting library categories usage`,
      ''
    )
  },
  loadLibraryCategoryStates(context: any): Promise<boolean> {
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: `${AppConst.libraryCategoryStatesUrl}`,
      },
      context,
      'updateLibraryCategoryStates',
      `getting library category states`
    )
  },
  createLibraryCategory(context: any, payload: LibraryCategoryCreatePayload): Promise<boolean> {
    let myTenantID: number = payload.TenantId

    if (myTenantID == null || myTenantID === TenantHelpers.InvalidTenantID) {
      myTenantID = this.getMyTenantID(context.rootState.app)
    }

    const createPayload: LibraryCategoryCreatePayload = {
      DisplayName: payload.DisplayName,
      CategoryOrder: payload.CategoryOrder || 0,
      StateId: payload.StateId,
      TenantId: myTenantID,
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.tenantsV1Url}/${myTenantID}${AppConst.APIEndpoint.libraryCategoriesUrlV2}`,
        body: createPayload,
      },
      context,
      'updateLibraryCategories',
      `create a new library category`,
      ''
    )
  },
  upsertLibraryCategories(context: any, payload: upsertLibraryCategoriesPayload): Promise<boolean> {
    if (!payload || payload.categories.length === 0) {
      consoleError('Empty upsertLibraryCategories payload (LibraryCategory[]) received:', payload)
      return Promise.resolve(false)
    } else {
      const tenantId: number | undefined = payload.tenantId

      const url: string = tenantId == null ? AppConst.APIEndpoint.libraryCategoriesUrl : `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${AppConst.APIEndpoint.libraryCategoriesUrlV2}`

      return apiRequest(
        {
          action: UtilsConst.HTTPAction.PUT,
          url,
          body: payload.categories,
        },
        context,
        '',
        `upserting library categories`
      )
    }
  },
  deleteLibraryCategoryById(context: any, categoryId: number): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    const url = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}${AppConst.APIEndpoint.libraryCategoriesUrlV2}/${categoryId}`
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: url,
      },
      context,
      '', // Don't call mutation - let the caller reload categories instead
      `deleting from library category, categoryId: ${categoryId}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  loadLibraryEntry(context: any, payload: any): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    const url = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}/library/v1/${payload.Id}`

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: url,
      },
      context,
      'updateLibraryEntry',
      `getting library entry details, Id: ${payload.Id}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  updateLibraryEntry(context: any, libraryEntry: LibraryEntry): Promise<boolean> {
    if (!libraryEntry) {
      consoleError('Empty updateLibraryEntry libraryEntry received:', libraryEntry)
      return Promise.resolve(false)
    }
    const tenantId = this.getMyTenantID(context.rootState.app)
    if (tenantId !== libraryEntry.TenantId) {
      return Promise.reject('mismatch between library entry tenant id and currently selected Tenant ID')
    }
    const url = `${AppConst.APIEndpoint.tenantsV1Url}/${libraryEntry.TenantId}/library/v1/${libraryEntry.Id}`
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.PUT,
        url: url,
        body: libraryEntry,
      },
      context,
      '',
      `updating tenant library entry ${libraryEntry.Id}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  deleteLibraryEntryById(context: any, id: string): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    const url = `${AppConst.APIEndpoint.tenantsV1Url}/${tenantId}/library/v1/${id}`
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: url,
      },
      context,
      'deleteFromModelsLibraryById',
      `deleting from models library, id: ${id}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  // uploadLibraryEntryThumbnail can be awaited like any other action, but internally it takes a little longer
  // because our image compression is done through a seperate queue and service. We poll to update the result of
  // the compression after we upload the initial png.
  async uploadLibraryEntryThumbnail(context: any, payload: UploadThumbnailRequest): Promise<boolean> {
    if (payload == null || payload.Id.length === 0 || !payload.TenantId || payload.Thumbnail.size === 0) {
      consoleError(
        `Invalid uploadLibraryEntryThumbnail payload. Required payload:
        UploadThumbnailRequest {
          Id: string
          TenantId: number
          Thumbnail: File
          IsFirstThumbnail: boolean
        }
      Received payload:`,
        payload
      )
      return Promise.resolve(false)
    } else {
      const file = new Blob([payload.Thumbnail], {
        type: 'application/octet-stream',
      })
      const formData = new FormData()
      // We change the thumbnail name before we send it as form data
      formData.append('thumbnail', file, `model-${payload.Id}.png`)
      const urlParams = {
        cl: '6x6', // Compression Level Block Size (copied from Unity client)
        mode: 'thorough', // Compression Speed (copied from Unity client)
      }
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${this.authService().accessToken}`,
          [UtilsConst.RequestHeaders.CustomHeaderKeyAppName]: AppConst.appName,
          [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.TenantId,
        },
        params: urlParams,
        timeout: 300 * 1000, // 5 minutes
      }
      const errorMsg = `uploading new library entry thumbnail. ID: ${payload.Id}, file: ${payload.Thumbnail.name}. `
      try {
        return await Axios.post<QueueIdResult>(
          `${AppConst.apiDomain}${AppConst.APIEndpoint.publicModelsUrl}/${payload.Id}/thumbnail/offline`,
          formData,
          config
        ).then(async (postImageResult) => {
          if (postImageResult.status === 200 && postImageResult.data.queueId !== '') {
            try {
              const queuedStatusResult = await pipeline.pollPipelineUntilDone(postImageResult.data.queueId, 20)
              context.commit('updateQueuedStatus', queuedStatusResult, { root: true })
              context.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
              context.commit(StoreConst.StoreHelper.SetApiErrorMutationName, '', { root: true })
              return true
            } catch (error) {
              onApiCatchError(context, Error(), errorMsg + (error as any).message)
              return false
            }
          } else {
            onApiCatchError(context, Error(), errorMsg + `QueueId missing from response.`)
            return false
          }
        })
      } catch (error) {
        onApiCatchError(context, error, errorMsg)
        return false
      }
    }
  },
  loadLibraryReferenceAssets(context: any, payload: LibraryReferenceAssetPayload): Promise<boolean> {
    const url = `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsReference}`

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url,
        params: payload,
      },
      context,
      'updateLibraryReferenceAssets',
      `getting library entry reference assets details`,
      StoreConst.StoreHelper.loadingMutationName,
      {},
      (context: any, error: any, errorMsg: string, onApiCatchError: any) => {
        if (error && error.response && error.response.status === 404) {
          // No reference assets found - clear any existing ones
          context.commit('updateLibraryReferenceAssets', {
            references: [] as LibraryReferenceAsset[],
          })
          context.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
          return Promise.resolve(true)
        } else {
          onApiCatchError(context, error, errorMsg)
          return Promise.resolve(false)
        }
      }
    )
  },
  async loadReferenceContextProcessingStatus(context: any, contextId: number): Promise<LoadContextProcessingStatusResult> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    if (tenantId == null || tenantId === TenantHelpers.InvalidTenantID || !contextId) {
      return {
        success: false,
        error: 'Invalid context request',
        statusCode: 400,
      }
    }

    try {
      const response = await Axios.get<ContextProcessingStatusResponse>(
        `${AppConst.APIEndpoint.senseV1Url}${AppConst.APIEndpoint.contextUrl}/${contextId}/status`,
        {
          baseURL: AppConst.apiDomain,
          headers: {
            Authorization: `Bearer ${this.authService().accessToken}`,
            [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
          },
        }
      )

      return {
        success: true,
        data: response.data,
        statusCode: response.status,
      }
    } catch (error) {
      const axiosError = error as any
      const response = axiosError && axiosError.response ? axiosError.response : null
      const responseMessage = response && response.data && response.data.message ? response.data.message : ''

      return {
        success: false,
        error: responseMessage || 'Failed to load context processing status',
        statusCode: response && response.status ? response.status : 0,
      }
    }
  },
  presignedFile(context: any, payload: any) {
    const tenantId = this.getMyTenantID(context.rootState.app)

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetPresigned}`,
        params: payload,
      },
      context,
      '',
      `presign library model reference file`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
  async uploadReferenceFile(context: any, payload: any) {
    const { mode, storage, file } = payload

    let url = ''

    if (mode === 'multipart') {
      url = payload[mode].action_url
    } else {
      url = payload[mode].url
    }

    const completeRequestPayload = {
      ...storage,
      assetType: 'document',
      originalFilename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadMod: mode,
    }

    try {
      if (mode === 'multipart') {
        // Multipart mode: POST with form-data
        const formData = new FormData()
        const fields = payload[mode].fields

        // Add all fields first
        if (fields) {
          Object.keys(fields).forEach(key => {
            formData.append(key, fields[key])
          })
        }

        // Then add the file
        formData.append('file', file)

        return await Axios.post(
          url,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        ).then((uploadResponse) => ({
          status: uploadResponse.status,
          data: completeRequestPayload,
        })).catch((error) => {
          onApiCatchError(context, Error(), `Something went wrong when upload _multipart_ library model reference file.`)
          return false
        })
      } else {
        // Single mode: PUT request
        return await Axios.put(
          url,
          file,
          {
            headers: {
              'Content-Type': payload[mode].headers['Content-Type'][0],
            }
          }
        ).then((uploadResponse) => ({
          status: uploadResponse.status,
          data: completeRequestPayload,
        })).catch((error) => {
          onApiCatchError(context, Error(), `Something went wrong when upload _single mode_ library model reference file.`)
          return false
        })
      }
    } catch (error: any) {
      onApiCatchError(context, error, error.message || `Something went wrong when upload library model reference file.`)
      return false
    }
  },
  completeLibraryReferenceFileUpload(context: any, payload: any) {
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsComplete}`,
        body: payload,
      },
      context,
      '',
      `complete library model reference file upload`,
      StoreConst.StoreHelper.loadingMutationName
    )
  },
  deleteLibraryReferenceAssetById(context: any, assetGuid: string): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsReference}/${assetGuid}`,
      },
      context,
      '',
      `deleting library reference asset, assetGuid: ${assetGuid}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  /**
   * List existing security certificates for the tenant.
   * Expecting 1-3 keys/certificates per tenant so we ignore pagination in results for now.
   * Returns { success: boolean, data?: any, error?: string } for the caller to handle.
   */
  async loadSecurityCertificates(context: any, params?: any): Promise<any> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    if (tenantId == null || tenantId === TenantHelpers.InvalidTenantID) {
      consoleWarn('loadSecurityCertificates called with invalid tenantId:', tenantId)
      return { success: false, error: 'Invalid tenant' }
    }

    let errorMessage = ''

    const result = await apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url: AppConst.APIEndpoint.securityCertificatesV1Url,
        params,
      },
      context,
      'updateSecurityCertificates',
      'getting security certificates',
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      },
      (ctx: any, error: any) => {
        ctx.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
        if (error.response) {
          errorMessage = (error.response.data && error.response.data.message) || `Something went wrong. Error code: ${error.response.status}`
        } else if (error.request) {
          errorMessage = 'No response received from server'
        } else {
          errorMessage = 'Something went wrong getting security certificates'
        }
      }
    )

    if (result === false) {
      return { success: false, error: errorMessage }
    }
    return { success: true, data: result }
  },

  /**
   * Generate a new security certificate for a user.
   * Requires tenant admin permission if creating for another user.
   * Returns { success: boolean, data?: any, error?: string } for the caller to handle.
   */
  async createSecurityCertificate(context: any, payload: CreateSecurityCertificatePayload): Promise<any> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    if (tenantId == null || tenantId === TenantHelpers.InvalidTenantID) {
      consoleWarn('createSecurityCertificate called with invalid tenantId:', tenantId)
      return { success: false, error: 'Invalid tenant' }
    }

    let errorMessage = ''

    const result = await apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: AppConst.APIEndpoint.securityCertificatesV1Url,
        body: payload,
      },
      context,
      '',
      'generating security certificate',
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      },
      (ctx: any, error: any) => {
        ctx.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
        if (error.response) {
          errorMessage = (error.response.data && error.response.data.message) || `Something went wrong. Error code: ${error.response.status}`
        } else if (error.request) {
          errorMessage = 'No response received from server'
        } else {
          errorMessage = 'Something went wrong generating security certificate'
        }
      }
    )

    if (result === false) {
      return { success: false, error: errorMessage }
    }
    return { success: true, data: result }
  },

  /**
   * Archive (delete) a security certificate by ID.
   * Must own the certificate or be a JigStaff user.
   * Returns { success: boolean, data?: any, error?: string } for the caller to handle.
   */
  async archiveSecurityCertificate(context: any, certificateId: number): Promise<any> {
    const tenantId = this.getMyTenantID(context.rootState.app)

    if (tenantId == null || tenantId === TenantHelpers.InvalidTenantID) {
      consoleWarn('archiveSecurityCertificate called with invalid tenantId:', tenantId)
      return { success: false, error: 'Invalid tenant' }
    }

    if (certificateId == null) {
      consoleWarn('archiveSecurityCertificate called with invalid certificateId:', certificateId)
      return { success: false, error: 'Invalid certificate ID' }
    }

    let errorMessage = ''

    const result = await apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: `${AppConst.APIEndpoint.securityCertificatesV1Url}/${certificateId}`,
      },
      context,
      '',
      `deleting security certificate, id: ${certificateId}`,
      StoreConst.StoreHelper.loadingMutationName,
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      },
      (ctx: any, error: any) => {
        ctx.commit(StoreConst.StoreHelper.loadingMutationName, false, { root: true })
        if (error.response) {
          errorMessage = (error.response.data && error.response.data.message) || `Something went wrong. Error code: ${error.response.status}`
        } else if (error.request) {
          errorMessage = 'No response received from server'
        } else {
          errorMessage = 'Something went wrong deleting security certificate'
        }
      }
    )

    if (result === false) {
      return { success: false, error: errorMessage }
    }
    return { success: true, data: result }
  },
}
